import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { debounceTime, distinctUntilChanged, of } from 'rxjs';
import { GroupService } from '@app/admin/services/group.service';
import { visibilityGroupActions } from './visibility-group.actions';
import { VisibilityGroup } from '@generated/generatedEntities';

@Injectable()
export class VisibilityGroupEffects {
  private actions$ = inject(Actions);
  private groupService = inject(GroupService);

  constructor() {}

  loadGroups$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(visibilityGroupActions.loadGroups),
      debounceTime(300),
      switchMap(({ organisationId }) =>
        this.groupService.getGroups(organisationId).pipe(
          map((groups) => visibilityGroupActions.loadGroupsSuccess({ groups })),
          catchError((error) => of(visibilityGroupActions.loadGroupsFailure({ error }))),
        ),
      ),
    );
  });

  createGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(visibilityGroupActions.createGroup),
      debounceTime(300),
      switchMap(({ organisationId, name }) =>
        this.groupService.createGroup(organisationId, name).pipe(
          map((group) => visibilityGroupActions.createGroupSuccess({ group })),
          catchError((error) => of(visibilityGroupActions.createGroupFailure({ error }))),
        ),
      ),
    );
  });

  deleteGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(visibilityGroupActions.deleteGroup),
      debounceTime(300),
      switchMap(({ groupId }) =>
        this.groupService.deleteGroup(groupId).pipe(
          map(() => visibilityGroupActions.deleteGroupSuccess({ groupId })),
          catchError((error) => of(visibilityGroupActions.deleteGroupFailure({ error }))),
        ),
      ),
    );
  });

  setDefaultGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(visibilityGroupActions.setDefaultGroup),
      tap((action) => console.log('setDefaultGroup action:', action)),
      debounceTime(300),
      distinctUntilChanged((previous, current) => {
        console.log(previous);
        return previous.groupId == current.groupId;
      }),
      switchMap(({ organisationId, groupId }) =>
        this.groupService.setDefaultGroup(organisationId, groupId).pipe(
          tap((response) => console.log('setDefaultGroup service response:', response)),
          map(() => {
            console.log('Dispatching setDefaultGroupSuccess');
            return visibilityGroupActions.setDefaultGroupSuccess({
              organisationId,
              groupId,
            });
          }),
          catchError((error) => {
            console.error('Error setting default group:', error);
            return of(visibilityGroupActions.setDefaultGroupFailure({ error }));
          }),
        ),
      ),
    );
  });

  renameGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(visibilityGroupActions.renameGroup),
      debounceTime(300),
      tap((action) => console.log('Renaming group:', action)),
      switchMap(({ groupId, newName }) =>
        this.groupService.renameGroup(groupId, newName).pipe(
          tap((group) => console.log('Group renamed successfully:', group)),
          map((group) => visibilityGroupActions.renameGroupSuccess({ group })),
          catchError((error) => {
            console.error('Error renaming group:', error);
            const errorMessage = error.message || 'Failed to rename group';
            return of(
              visibilityGroupActions.renameGroupFailure({
                error: errorMessage,
              }),
            );
          }),
        ),
      ),
    );
  });

  updateSelectedGroups$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(visibilityGroupActions.updateSelectedGroups),
      debounceTime(300),
      switchMap(({ userId, groupIds }) =>
        this.groupService.updateSelectedGroups(userId, groupIds).pipe(
          map(() => visibilityGroupActions.updateSelectedGroupsSuccess({ userId, groupIds })),
          catchError((error) => of(visibilityGroupActions.updateSelectedGroupsFailure({ error }))),
        ),
      ),
    );
  });

  addUserToGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(visibilityGroupActions.addUserToGroup),
      debounceTime(300),
      switchMap(({ organisationId, userEmail, groupId }) =>
        this.groupService.addUserToGroup(organisationId, userEmail, groupId).pipe(
          map(() => visibilityGroupActions.addUserToGroupSuccess({ organisationId, userEmail, groupId })),
          catchError((error) => of(visibilityGroupActions.addUserToGroupFailure({ error }))),
        ),
      ),
    );
  });

  updateInviteGroups$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(visibilityGroupActions.updateInviteGroups),
      debounceTime(300),
      switchMap(({ invite }) =>
        this.groupService.updateInviteGroups(invite).pipe(
          map(() => visibilityGroupActions.updateInviteGroupsSuccess({ invite })),
          catchError((error) => of(visibilityGroupActions.updateInviteGroupsFailure({ error }))),
        ),
      ),
    );
  });

  toggleDefaultGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(visibilityGroupActions.updateDefaultGroups),
      tap((action) => console.log('toggleDefaultGroup action:', action)),
      debounceTime(300),
      distinctUntilChanged((previous, current) => {
        console.log(previous);
        return this.compareGroups(previous.groups, current.groups);
      }),
      switchMap(({ organisationId, groups }) => {
        return this.groupService.updateDefaultGroups(organisationId, groups).pipe(
          tap((response) => console.log('toggleDefaultGroup service response:', response)),
          map(() =>
            visibilityGroupActions.updateDefaultGroupsSuccess({
              groups,
            }),
          ),
          catchError((error) => {
            console.error('Error toggling default group:', error);
            return of(visibilityGroupActions.updateDefaultGroupsFailure({ error }));
          }),
        );
      }),
    );
  });

  private compareGroups(previous: VisibilityGroup[], current: VisibilityGroup[]): boolean {
    if (previous.length !== current.length) {
      return false;
    }
    for (let i = 0; i < previous.length; i++) {
      if (previous[i].id !== current[i].id || previous[i].default !== current[i].default) {
        return false;
      }
    }
    return true;
  }
}
